import * as React from 'react';
import * as I18n from 'i18n-js';
import gql from 'graphql-tag';
import { MutationFn, graphql } from 'react-apollo';
import {
  usePlaidLink,
  PlaidLinkOnSuccess,
  PlaidLinkOnEvent,
  PlaidLinkOnExit,
} from 'react-plaid-link';
import { flowRight } from 'lodash';
import { trackEvent, MParticleEventName } from '@reverbdotcom/commons/dist/elog/mparticle_tracker';
import * as elog from '@reverbdotcom/commons/dist/elog';
import AlertBox from '@reverbdotcom/commons/dist/components/alert_box';
import FormGroup from '@reverbdotcom/commons/dist/components/form_group';

const PLAID_EVENT_TYPES_FOR_TRACKING = ['OPEN', 'EXIT', 'HANDOFF'];
const PLAID_ERROR_EVENT_TYPE = 'ERROR';
const COUNTRY_CODES = ['US', 'CA'];

interface MutationProps {
  createMyPlaidLinkToken?: MutationFn<any, any>;
  savePlaidAccountMutationFn?: MutationFn<any, any>;
}

interface ExternalProps {
  summaryConfig: {
    setPlaidModalOpenInSummaryView: (callback: () => void) => void;
    refetchDcp?: () => void;
    setSummaryLoadingState?: (boolean) => void;
    setPlaidAccountStatus: (string) => void;
  },
  openHandlerOnly?: boolean;
  shopCountryCode: string;
}

type Props = MutationProps & ExternalProps;

export function PlaidLinkContainer({
  createMyPlaidLinkToken,
  openHandlerOnly,
  summaryConfig,
  savePlaidAccountMutationFn,
  shopCountryCode,
}: Props) {
  const [selectedProfileCountryCode, setSelectedProfileCountryCode] = React.useState(shopCountryCode === 'US' ? 'US' : null);
  const [plaidConnectionInitiated, setPlaidConnectionInitiated] = React.useState(!!selectedProfileCountryCode);

  const [plaidLinkToken, setPlaidLinkToken] = React.useState(null);
  const urlParams = React.useMemo(
    () => new URLSearchParams(window.location.search),
    [],
  );
  const [oauthStateId, setOAuthStateId] = React.useState(
    urlParams.get('oauth_state_id'),
  );
  const tokenInLocalStorage = React.useMemo(
    () => localStorage.getItem('plaid-link-token'),
    [],
  );
  const [fetchNewToken, setFetchNewToken] = React.useState(null);
  const [hasError, setHasError] = React.useState(null);

  const handleExit = React.useCallback((refreshDCP: boolean | undefined) => {
    if (refreshDCP && summaryConfig.refetchDcp) {
      summaryConfig.refetchDcp();
    } else {
      // eslint-disable-next-line no-restricted-globals
      if (!history.pushState) {
        return;
      }
      setOAuthStateId(null);
      const newUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
      window.history.pushState({ path: newUrl }, '', newUrl);
    }
  }, [summaryConfig]);

  async function savePlaidAccount(publicToken, metadata) {
    const accountId = metadata?.accounts[0]?.id;
    const verificationStatus = metadata?.accounts[0]?.verification_status;
    setHasError(false);
    try {
      summaryConfig.setSummaryLoadingState(true);
      const result: any | { data } = await savePlaidAccountMutationFn({
        variables: { input: { accountId, publicToken, verificationStatus, countryCode: selectedProfileCountryCode } },
      });
      const plaidAccountIdExists =
        result.data.saveMyPlaidAccount.plaidAccountId;
      summaryConfig.setPlaidAccountStatus(verificationStatus);

      if (plaidAccountIdExists) {
        summaryConfig.setSummaryLoadingState(false);
        handleExit(!verificationStatus?.includes('pending_automatic'));
      }
    } catch (error) {
      elog.error(
        'plaid.frontend.savePlaidAccount.error',
        {
          metadata,
          errorMessage: error?.message,
          plaidAccountId: accountId,
        },
        error,
      );
      setHasError(true);
      summaryConfig.setSummaryLoadingState(false);
    }
  }

  React.useEffect(() => {
    async function createLinkToken(mutationFn) {
      setHasError(false);
      if (oauthStateId && tokenInLocalStorage && !fetchNewToken) {
        setPlaidLinkToken(tokenInLocalStorage);
        return;
      }
      if (!selectedProfileCountryCode) {
        return;
      }
      try {
        const result = await mutationFn({ variables: { input: { countryCode: selectedProfileCountryCode } } });
        const linkToken = result?.data?.createMyPlaidLinkToken.linkToken;
        setPlaidLinkToken(linkToken);
        localStorage.setItem('plaid-link-token', linkToken);
      } catch (error) {
        elog.error(
          'plaid.frontend.createLinkToken.error',
          {
            errorMessage: error?.message,
          },
          error,
        );
        setHasError(true);
      }
    }
    createLinkToken(createMyPlaidLinkToken);
  }, [
    createMyPlaidLinkToken,
    tokenInLocalStorage,
    oauthStateId,
    fetchNewToken,
    selectedProfileCountryCode,
  ]);

  const handlers = {
    savePlaidAccount,
    handleExit,
    setFetchNewToken,
  };
  const { setPlaidModalOpenInSummaryView } = summaryConfig;

  const changeCountryCode = (event) => {
    setSelectedProfileCountryCode(event.target.value);
  };

  const togglePlaidConnectionInitiated = () => {
    setPlaidConnectionInitiated(!plaidConnectionInitiated);
  };

  return <>
    {hasError && <AlertBox type="error">
      {I18n.t('discovery.plaid.errorMsg')}
    </AlertBox>}
    {shopCountryCode === 'CA' && (
      plaidConnectionInitiated ? (
        <div className="scaling-mtb-3">
          <FormGroup label="Please select the country your institution is in:">
            <div className="form-group__fields">
              { COUNTRY_CODES.map(countryCode => {
                return (
                  <div key={countryCode} className="label-with-radio">
                    <input
                      type="radio"
                      name="shop[country_code]"
                      id={`shop_country_code_${countryCode}`}
                      value={countryCode}
                      onChange={changeCountryCode}
                    />
                    <label htmlFor="shop[country_code]">
                      {I18n.t(`countries.${countryCode}`)}
                    </label>
                  </div>);
              })
              }
            </div>
          </FormGroup>
        </div>
      ) : (
        <button
          className="button button--blue mtb-3"
          type="button"
          onClick={togglePlaidConnectionInitiated}
        >
          {I18n.t('discovery.plaid.startConnectingWithPlaid')}
        </button>
      )
    )}
    {plaidConnectionInitiated && plaidLinkToken && <PlaidLink
      handlers={handlers}
      isOauth={oauthStateId}
      linkToken={plaidLinkToken}
      openHandlerOnly={openHandlerOnly && shopCountryCode === 'US'}
      setPlaidModalOpenInSummaryView={setPlaidModalOpenInSummaryView}
    />}
    {plaidConnectionInitiated && !plaidLinkToken && !openHandlerOnly && <button className="button button--blue mt-1" type="button" disabled>
      {I18n.t('discovery.plaid.loading')}
    </button>
    }
    <p className="hint">{I18n.t('discovery.plaid.disclaimer')}</p>
  </>;
}

function PlaidLink({
  linkToken,
  isOauth,
  handlers,
  setPlaidModalOpenInSummaryView,
  openHandlerOnly,
}) {

  const onSuccess = React.useCallback<PlaidLinkOnSuccess>(
    (public_token, metadata) => {
      handlers.savePlaidAccount(public_token, metadata);
    },
    [handlers],
  );

  const onExit = React.useCallback<PlaidLinkOnExit>(
    (err) => {
      if (err && err.error_code === 'INVALID_LINK_TOKEN') {
        handlers.setFetchNewToken(true);
      }
      handlers.handleExit();
    },
    [handlers],
  );

  const onEvent = React.useCallback<PlaidLinkOnEvent>(
    (eventName, metadata) => {
      if (PLAID_EVENT_TYPES_FOR_TRACKING.includes(eventName)) {
        trackEvent({
          eventName: MParticleEventName.PlaidLink,
          plaidLinkSessionId: metadata.link_session_id,
          plaidExitStatus: metadata.exit_status,
          plaidErrorMessage: metadata.error_message,
          plaidEventName: eventName,
        });
      }

      if (eventName === PLAID_ERROR_EVENT_TYPE) {
        elog.error(
          'plaid.frontend.plaidModal.error',
          {
            plaidErrorType: metadata.error_type,
            plaidErrorMessage: metadata.error_message,
            institutionId: metadata.institution_id,
            plaidRequestId: metadata.request_id,
          },
          `plaid.frontend.plaidModal.error.${metadata.error_code}`,
        );
      }
    },
    [],
  );

  const config: any = {
    token: linkToken,
    onSuccess,
    onExit,
    onEvent,
    receivedRedirectUri: isOauth ? document.location.href : null,
  };
  const { ready, open } = usePlaidLink(config);

  React.useEffect(() => {
    if (open && open.name === 'open') {
      setPlaidModalOpenInSummaryView(open);
    }
    if (isOauth && ready) {
      open();
    }
  }, [open, ready, setPlaidModalOpenInSummaryView, isOauth]);

  const handleClick = React.useCallback(
    (event) => {
      event.preventDefault();
      if (ready) {
        open();
      }
    },
    [open, ready],
  );

  return (
    <>
      {openHandlerOnly ? (
        <button
          data-change-link
          className="button-as-link mtb-1"
          dangerouslySetInnerHTML={{ __html: I18n.t(
            'discovery.plaid.connectADifferentAccountWithPlaidHtml',
            { icon: '<span class="fa fa-pencil"></span>' },
          ) }}
          type="button"
          onClick={handleClick}
          disabled={!ready}
        />
      ) :
        (<button
          className="button button--blue mtb-1"
          type="button"
          onClick={handleClick}
          disabled={!ready}
        >
          {I18n.t('discovery.plaid.connect')}
        </button>)}
    </>
  );
}

export const CREATE_LINK_TOKEN_MUTATION = gql`
  mutation Core_Payouts_CreateMyPlaidLinkToken(
    $input: Input_core_apimessages_CreateMyPlaidLinkTokenRequest
  ) {
    createMyPlaidLinkToken(input: $input) {
      linkToken
    }
  }
`;

export const SAVE_PLAID_ACCOUNT_MUTATION = gql`
  mutation Core_Payouts_SaveMyPlaidAccount(
    $input: Input_core_apimessages_SaveMyPlaidAccountRequest
  ) {
    saveMyPlaidAccount(input: $input) {
      plaidAccountId
    }
  }
`;

const withMutation = graphql<any, any, any>(CREATE_LINK_TOKEN_MUTATION, {
  name: 'createMyPlaidLinkToken',
  options: {
    errorPolicy: 'all',
  },
});

const withSaveMutation = graphql<any, any, any>(SAVE_PLAID_ACCOUNT_MUTATION, {
  name: 'savePlaidAccountMutationFn',
  options: {
    errorPolicy: 'all',
  },
});
export default flowRight([withMutation, withSaveMutation])(PlaidLinkContainer);



// WEBPACK FOOTER //
// ./node_modules/@reverbdotcom/discovery-ui/src/components/plaid_link.tsx